/**
 * CRO fixes Oct 2026: "Added to your bag" confirmation after Add to cart.
 * Shows the product, the amount left to free parcel-locker delivery, and
 * [View cart] / [Continue shopping]. Strings come from #dlx-toast-config
 * (rendered in snippets/cart-icon-component.liquid, 5 languages).
 */
import { ThemeEvents } from '@theme/events';

if (!window.__dlxAddedToast) {
  window.__dlxAddedToast = true;

  const configElement = document.getElementById('dlx-toast-config');
  let config = null;
  try {
    config = configElement ? JSON.parse(configElement.textContent) : null;
  } catch (_) {
    config = null;
  }

  if (config) {
    const threshold = config.threshold || 7000;
    const t = config.t;
    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';

    const money = (cents) => {
      try {
        return new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency || 'EUR' }).format(cents / 100);
      } catch (_) {
        return (cents / 100).toFixed(2) + ' €';
      }
    };

    const escapeHtml = (value) =>
      String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

    const toast = document.createElement('div');
    toast.className = 'dlx-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.hidden = true;
    document.body.appendChild(toast);

    let hideTimer;
    const close = () => {
      clearTimeout(hideTimer);
      toast.classList.remove('dlx-toast--open');
      setTimeout(() => {
        toast.hidden = true;
      }, 250);
    };

    toast.addEventListener('click', (event) => {
      if (event.target.closest('[data-dlx-close]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
      const data = event.detail && event.detail.data;
      if (!data || data.source !== 'product-form-component' || data.didError) return;
      if (/\/cart\/?$/.test(window.location.pathname)) return;

      let cart;
      try {
        const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        cart = await response.json();
      } catch (_) {
        return;
      }

      const variantId = Number(event.detail.sourceId);
      const item = (cart.items || []).find((i) => i.variant_id === variantId) || (cart.items || [])[0];
      if (!item) return;

      const remaining = threshold - cart.total_price;
      const shipping =
        remaining > 0
          ? escapeHtml(t.remaining).replace('{amount}', '<strong>' + escapeHtml(money(remaining)) + '</strong>')
          : '<strong>' + escapeHtml(t.unlocked) + '</strong>';

      const image = item.image
        ? '<img src="' + escapeHtml(item.image + (item.image.includes('?') ? '&' : '?') + 'width=160') + '" alt="" width="64" height="64">'
        : '';
      const variant =
        item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="dlx-toast__variant">' + escapeHtml(item.variant_title) + '</p>'
          : '';

      toast.innerHTML =
        '<div class="dlx-toast__inner">' +
        '<div class="dlx-toast__head"><span class="dlx-toast__check" aria-hidden="true">✓</span>' +
        '<span class="dlx-toast__title">' + escapeHtml(t.added) + '</span>' +
        '<button type="button" class="dlx-toast__close" data-dlx-close aria-label="' + escapeHtml(t.close) + '">×</button></div>' +
        '<div class="dlx-toast__item">' + image +
        '<div class="dlx-toast__text"><p class="dlx-toast__name">' + escapeHtml(item.product_title) + '</p>' + variant +
        '<p class="dlx-toast__price">' + escapeHtml(money(item.final_line_price)) + '</p></div></div>' +
        '<p class="dlx-toast__ship">' + shipping + '</p>' +
        '<div class="dlx-toast__actions">' +
        '<a class="button dlx-toast__cart" href="' + escapeHtml(root + 'cart') + '">' + escapeHtml(t.view) + '</a>' +
        '<button type="button" class="button button-secondary dlx-toast__continue" data-dlx-close>' + escapeHtml(t.cont) + '</button>' +
        '</div></div>';

      toast.hidden = false;
      requestAnimationFrame(() => toast.classList.add('dlx-toast--open'));

      clearTimeout(hideTimer);
      hideTimer = setTimeout(close, 9000);
    });
  }
}
